import classNames from 'classnames';
import React, { useMemo } from 'react';
import HorizontalScrollWithFade from '../../components/HorizontalScrollWithFade';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import topicIcon from '../../icons/prefilled/16x16/topic.svg';
import { isFeatureEnabled } from '../../utils/features';
import sortTopicsByName from '../../utils/sortTopicsByName';
import PostTopicAdd from './PostTopicAdd';
import styles from './PostTopicListWithBreadcrumbs.module.scss';
import PostTopicWithBreadcrumbs from './PostTopicWithBreadcrumbs';

const TOPIC_FRAGMENT = gql(`
  fragment PostTopicListWithBreadcrumbs_topic on PublicTopicFields {
    id
    name
    ...PostTopicWithBreadcrumbs_topic
    ...PostTopicAdd_topic
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_FRAGMENT>;

const POST_FRAGMENT = gql(`
  fragment PostTopicListWithBreadcrumbs_post on Post {
    id
    ...PostTopicWithBreadcrumbs_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

type PostTopicAddProps = React.ComponentProps<typeof PostTopicAdd>;
type PostTopicWithBreadcrumbsProps = React.ComponentProps<
  typeof PostTopicWithBreadcrumbs
>;

type PostTopicListWithBreadcrumbsProps = {
  onAdd?: PostTopicAddProps['onAdd'];
  onCreate?: PostTopicAddProps['onCreate'];
  onRemove?: PostTopicWithBreadcrumbsProps['onRemove'];
  postTopics: TopicFragment[];
  large?: boolean;
} & (
  | { editing?: false; post?: never }
  | {
      editing: true;
      onAdd: PostTopicAddProps['onAdd'];
      onCreate: PostTopicAddProps['onCreate'];
      onRemove: PostTopicWithBreadcrumbsProps['onRemove'];
      post: PostFragment;
    }
);

export const hasBreadcrumbs = (
  postTopics: TopicFragment[],
  editing?: boolean,
) => postTopics && (postTopics.length > 0 || editing);

const PostTopicListWithBreadcrumbs = ({
  postTopics,
  large,
  ...props
}: PostTopicListWithBreadcrumbsProps) => {
  const sortedTopics = useMemo(
    () => sortTopicsByName(postTopics),
    [postTopics],
  );

  if (!hasBreadcrumbs(postTopics, props.editing)) {
    return null;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.large]: large,
        [styles.newPostLayout]: isFeatureEnabled('new_post_layout'),
      })}
    >
      <SVGIcon inline className={styles.icon} size={18} role="presentation">
        {topicIcon}
      </SVGIcon>
      <div className={styles.scrollerContainer}>
        <HorizontalScrollWithFade>
          <div className={styles.wrapper}>
            <ul className={styles.topicContainer} aria-label="Post topics">
              {sortedTopics.map((topic) => (
                <li
                  key={topic.id}
                  className={styles.topic}
                  aria-label={`Topic: ${topic.name}`}
                >
                  <PostTopicWithBreadcrumbs
                    topic={topic}
                    large={large}
                    {...props}
                  />
                </li>
              ))}
              {props.editing && (
                <li className={styles.topic} aria-label="Add a topic">
                  <PostTopicAdd
                    onAdd={props.onAdd}
                    onCreate={props.onCreate}
                    postTopics={sortedTopics}
                    large={large}
                  />
                </li>
              )}
            </ul>
          </div>
        </HorizontalScrollWithFade>
      </div>
    </div>
  );
};

export default PostTopicListWithBreadcrumbs;
